import React, { useState, useEffect } from 'react';
import api, { openDocument } from '../api';
import { format } from 'date-fns';

const Recruits = () => {
  const [recruits, setRecruits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', email: '', job_title: '', department: '', start_date: '', pay_rate: '', weekly_hours: '41.5' });

  const [manage, setManage] = useState(null);   // recruit being managed
  const [docs, setDocs] = useState([]);
  const [assign, setAssign] = useState({ title: '', category: 'contract', requires_signature: true, file: null });
  const [contractPay, setContractPay] = useState({ pay_rate: '', weekly_hours: '41.5' });
  const [entitlement, setEntitlement] = useState(28);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/recruitment/recruits');
      setRecruits(r.data);
    } catch (err) {
      setError('Failed to load recruits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openManage = async (r) => {
    setManage(r);
    setEntitlement(28);
    setAssign({ title: '', category: 'contract', requires_signature: true, file: null });
    setContractPay({ pay_rate: r.pay_rate != null ? r.pay_rate : '', weekly_hours: r.weekly_hours != null ? r.weekly_hours : '41.5' });
    setError('');
    try {
      const res = await api.get(`/api/documents/recruit/${r.id}`);
      setDocs(res.data);
    } catch (err) { setDocs([]); }
  };

  const refreshDocs = async () => {
    const res = await api.get(`/api/documents/recruit/${manage.id}`);
    setDocs(res.data);
  };

  const addRecruit = async () => {
    if (!addForm.name || !addForm.email) return setError('Name and email required');
    setBusy(true);
    try {
      const r = await api.post('/api/recruitment/recruits', addForm);
      setSuccess(r.data.message || 'Recruit added');
      setAddOpen(false);
      setAddForm({ name: '', email: '', job_title: '', department: '', start_date: '', pay_rate: '', weekly_hours: '41.5' });
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add recruit');
    } finally { setBusy(false); }
  };

  const generateContract = async () => {
    if (!contractPay.pay_rate) return setError('Enter the hourly pay rate to generate the contract');
    setBusy(true);
    setError('');
    try {
      await api.post(`/api/documents/recruit/${manage.id}/generate-contract`, {
        pay_rate: contractPay.pay_rate,
        weekly_hours: contractPay.weekly_hours || 41.5,
      });
      setSuccess('Contract generated and sent to the recruit to sign');
      refreshDocs();
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate contract');
    } finally { setBusy(false); }
  };

  const doAssign = async (e) => {
    e.preventDefault();
    if (!assign.file) return setError('Choose a file');
    if (!assign.title) return setError('Give the document a title');
    setBusy(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', assign.file);
      fd.append('title', assign.title);
      fd.append('category', assign.category);
      fd.append('requires_signature', assign.requires_signature);
      await api.post(`/api/documents/recruit/${manage.id}/assign`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSuccess('Document sent to recruit');
      setAssign({ title: '', category: 'contract', requires_signature: true, file: null });
      e.target.reset();
      refreshDocs();
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to assign');
    } finally { setBusy(false); }
  };

  const convert = async () => {
    if (!window.confirm(`Convert ${manage.name} to a staff member?`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/api/recruitment/recruits/${manage.id}/convert`, { holiday_entitlement: Number(entitlement) });
      setSuccess(r.data.message || 'Converted to staff');
      setManage(null);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to convert');
    } finally { setBusy(false); }
  };

  const assignedDocs = docs.filter(d => d.scope === 'assigned');
  const uploadedDocs = docs.filter(d => d.scope === 'upload');

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Onboarding</h1>
          <p className="page-subtitle">New starters completing their paperwork.</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setAddOpen(true); setError(''); }}>+ Add Recruit</button>
      </div>

      {error && <div className="alert alert-error" onClick={() => setError('')}>{error}</div>}
      {success && <div className="alert alert-success" onClick={() => setSuccess('')}>{success}</div>}

      <div className="card">
        {loading ? (
          <div className="loader">Loading...</div>
        ) : recruits.length === 0 ? (
          <div className="empty-state"><h3>No one onboarding</h3><p>Add a recruit, or hire a candidate from the pipeline.</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Name</th><th>Job Title</th><th>Start Date</th><th>Contract</th><th>Uploads</th><th></th></tr>
              </thead>
              <tbody>
                {recruits.map(r => {
                  const signed = Number(r.docs_signed) >= Number(r.docs_to_sign) && Number(r.docs_to_sign) > 0;
                  return (
                    <tr key={r.id}>
                      <td style={{ fontWeight: 500 }}>{r.name}<div style={{ fontSize: '11px', color: 'var(--grey-mid)', fontWeight: 400 }}>{r.email}</div></td>
                      <td>{r.job_title || '—'}</td>
                      <td style={{ fontSize: '13px' }}>{r.start_date ? format(new Date(r.start_date + 'T00:00:00'), 'd MMM yyyy') : '—'}</td>
                      <td>
                        {Number(r.docs_to_sign) === 0
                          ? <span style={{ color: 'var(--grey-mid)', fontSize: '12px' }}>Not sent</span>
                          : signed ? <span className="badge badge-approved">Signed</span>
                          : <span className="badge badge-pending">{r.docs_signed}/{r.docs_to_sign}</span>}
                      </td>
                      <td>{r.uploads}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="btn btn-outline btn-sm" onClick={() => openManage(r)}>Manage</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add recruit */}
      {addOpen && (
        <div className="modal-overlay" onClick={() => setAddOpen(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Add Recruit</h3>
              <button className="modal-close" onClick={() => setAddOpen(false)}>&times;</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}
              <p style={{ color: 'var(--grey-mid)', fontSize: '13px', marginBottom: '16px' }}>Creates their onboarding login and emails their details.</p>
              <div className="form-group"><label className="form-label">Full Name</label><input className="form-control" value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} /></div>
              <div className="form-group"><label className="form-label">Email</label><input type="email" className="form-control" value={addForm.email} onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div className="form-group"><label className="form-label">Job Title</label><input className="form-control" value={addForm.job_title} onChange={e => setAddForm(f => ({ ...f, job_title: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">Start Date</label><input type="date" className="form-control" value={addForm.start_date} onChange={e => setAddForm(f => ({ ...f, start_date: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">Pay Rate (£/hour)</label><input type="number" min="0" step="0.01" className="form-control" value={addForm.pay_rate} onChange={e => setAddForm(f => ({ ...f, pay_rate: e.target.value }))} placeholder="e.g. 12.50" /></div>
                <div className="form-group"><label className="form-label">Weekly Hours</label><input type="number" min="0" step="0.5" className="form-control" value={addForm.weekly_hours} onChange={e => setAddForm(f => ({ ...f, weekly_hours: e.target.value }))} /></div>
              </div>
              <p style={{ fontSize: '11px', color: 'var(--grey-mid)' }}>Pay details are used to auto-fill their contract.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setAddOpen(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={addRecruit} disabled={busy}>{busy ? 'Adding...' : 'Add & email login'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Manage recruit */}
      {manage && (
        <div className="modal-overlay" onClick={() => setManage(null)}>
          <div className="modal" style={{ maxWidth: '620px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{manage.name}</h3>
              <button className="modal-close" onClick={() => setManage(null)}>&times;</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}

              <div style={{ fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--grey-mid)', fontWeight: 600, marginBottom: '8px' }}>Contracts / documents sent</div>
              {assignedDocs.length === 0 ? <p style={{ fontSize: '13px', color: 'var(--grey-mid)' }}>None sent yet.</p> : (
                <div className="table-wrap"><table>
                  <tbody>
                    {assignedDocs.map(d => (
                      <tr key={d.id}>
                        <td style={{ fontWeight: 500 }}>{d.title}</td>
                        <td>{d.requires_signature ? (d.signature ? <span className="badge badge-approved">Signed</span> : <span className="badge badge-pending">Awaiting signature</span>) : <span style={{ color: 'var(--grey-mid)', fontSize: '12px' }}>—</span>}</td>
                        <td style={{ fontSize: '11px', color: 'var(--grey-mid)' }}>{d.signature ? `${d.signature.typed_name}, ${format(new Date(d.signature.signed_at), 'd MMM yyyy HH:mm')}` : ''}</td>
                        <td style={{ textAlign: 'right' }}><button className="btn btn-outline btn-sm" onClick={() => openDocument(d.id)}>View</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              )}

              <div style={{ background: 'rgba(38,34,97,0.04)', border: '1px solid var(--border)', borderRadius: '6px', padding: '14px', margin: '14px 0' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--navy)', marginBottom: '4px' }}>Generate contract from template</div>
                <p style={{ fontSize: '12px', color: 'var(--grey-mid)', margin: '0 0 10px' }}>Auto-fills {manage.name}'s name, start date and job title. Enter their pay to complete it.</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '12px', alignItems: 'flex-end' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Pay rate (£/hour)</label><input type="number" min="0" step="0.01" className="form-control" value={contractPay.pay_rate} onChange={e => setContractPay(p => ({ ...p, pay_rate: e.target.value }))} placeholder="e.g. 12.50" /></div>
                  <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Weekly hours</label><input type="number" min="0" step="0.5" className="form-control" value={contractPay.weekly_hours} onChange={e => setContractPay(p => ({ ...p, weekly_hours: e.target.value }))} /></div>
                  <button className="btn btn-primary" onClick={generateContract} disabled={busy}>{busy ? 'Generating...' : 'Generate & send'}</button>
                </div>
              </div>

              <form onSubmit={doAssign} style={{ background: 'var(--grey-light)', border: '1px solid var(--border)', borderRadius: '6px', padding: '14px', margin: '14px 0' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--navy)', marginBottom: '10px' }}>Or upload a document (contract / offer)</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Title</label><input className="form-control" value={assign.title} onChange={e => setAssign(a => ({ ...a, title: e.target.value }))} placeholder="e.g. Contract of Employment" /></div>
                  <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Type</label>
                    <select className="form-control" value={assign.category} onChange={e => setAssign(a => ({ ...a, category: e.target.value }))}>
                      <option value="contract">Contract</option><option value="offer">Offer letter</option><option value="other">Other</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">File</label><input type="file" className="form-control" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" onChange={e => setAssign(a => ({ ...a, file: e.target.files[0] }))} /></div>
                  <div className="form-group" style={{ marginBottom: 0, display: 'flex', alignItems: 'flex-end' }}>
                    <label style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '12px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={assign.requires_signature} onChange={e => setAssign(a => ({ ...a, requires_signature: e.target.checked }))} style={{ accentColor: 'var(--red)' }} /> Require signature
                    </label>
                  </div>
                </div>
                <button className="btn btn-primary btn-sm" style={{ marginTop: '12px' }} disabled={busy}>{busy ? 'Sending...' : 'Send document'}</button>
              </form>

              <div style={{ fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--grey-mid)', fontWeight: 600, marginBottom: '8px' }}>Documents they've uploaded</div>
              {uploadedDocs.length === 0 ? <p style={{ fontSize: '13px', color: 'var(--grey-mid)' }}>Nothing uploaded yet.</p> : (
                <div className="table-wrap"><table>
                  <tbody>
                    {uploadedDocs.map(d => (
                      <tr key={d.id}>
                        <td style={{ fontWeight: 500 }}>{d.title}</td>
                        <td style={{ textTransform: 'capitalize', fontSize: '12px', color: 'var(--grey-mid)' }}>{(d.category || '').replace('_', ' ')}</td>
                        <td style={{ textAlign: 'right' }}><button className="btn btn-outline btn-sm" onClick={() => openDocument(d.id)}>View</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              )}
            </div>
            <div className="modal-footer" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: 'var(--grey-mid)' }}>Entitlement</span>
                <input type="number" min="0" max="50" step="0.5" className="form-control" style={{ width: '70px' }} value={entitlement} onChange={e => setEntitlement(e.target.value)} />
                <button className="btn btn-success" onClick={convert} disabled={busy}>Convert to staff</button>
              </div>
              <button className="btn btn-outline" onClick={() => setManage(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Recruits;
